import {
  Build,
  Component,
  Element,
  Host,
  Prop,
  State,
  Watch,
  h,
} from '@stencil/core';
import { createColorClasses } from '../../utils/theme';
import { getSvgContent, iconContent } from './request';
import { getName, getUrl } from './utils';
import type { Color } from '../../interface';

const CANSHADOW = !!document.head.attachShadow;

/**
 * An SVG loader / helper. Comes bundled with all fontawesome pro icons by default but can use any custom SVG (with an correct path). Lazily loads SVG for each icon, so will only request the icons that you're using and that are visible.
 */
@Component({
  tag: 'nano-icon',
  assetsDirs: ['../../../node_modules/@fortawesome/fontawesome-pro/svgs'],
  styleUrl: 'icon.scss',
  shadow: true,
})
export class Icon {
  private io?: IntersectionObserver;

  @Element() el!: HTMLNanoIconElement;

  @State() private svgContent?: string;
  @State() private isVisible = false;
  @State() private isLoading = true;

  /**
   * Color to use from your application's color palette.
   * Default options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop() color?: Color;

  /**
   * Specifies the label to use for accessibility. Defaults to the icon name.
   */
  @Prop({ mutable: true, reflect: true }) ariaLabel: string;

  /**
   * Specifies whether the icon should horizontally flip when `dir` is `"rtl"`.
   */
  @Prop() flipRtl?: boolean;

  /**
   * Specifies which icon to use from the built-in set of icons.
   */
  @Prop() name?: string;

  /**
   * Specifies the exact `src` of an SVG file to use.
   */
  @Prop() src?: string;

  /**
   * A combination of both `name` and `src`. If a `src` url is detected
   * it will set the `src` property. Otherwise it assumes it's a built-in named
   * SVG and set the `name` property.
   */
  @Prop() icon?: any;

  /**
   * The size of the icon.
   * Available options are: `"small"` and `"large"`.
   */
  @Prop() size?: 'small' | 'large';

  /**
   * If enabled, nano-icon will be loaded lazily when it's visible in the viewport.
   * Default, `false`.
   */
  @Prop() lazy = true;

  private setAriaLabel() {
    if (!this.ariaLabel) {
      const label = getName(this.name, this.icon);
      // user did not provide a label
      // come up with the label based on the icon name
      if (label) {
        this.ariaLabel = label.split('/').slice(-1)[0].replace(/\-/g, ' ');
      }
    }
  }

  connectedCallback() {
    // purposely do not return the promise here because loading
    // the svg file should not hold up loading the app
    // only load the svg if it's visible
    this.waitUntilVisible(this.el, '50px', () => {
      this.isVisible = true;
      this.loadIcon();
    });
  }

  disconnectedCallback() {
    if (this.io) {
      this.io.disconnect();
      this.io = undefined;
    }
  }

  componentWillLoad() {
    this.setAriaLabel();
  }

  private waitUntilVisible(
    el: HTMLElement,
    rootMargin: string,
    cb: () => void
  ) {
    if (
      Build.isBrowser &&
      CANSHADOW &&
      this.lazy &&
      typeof window !== 'undefined' &&
      (window as any).IntersectionObserver
    ) {
      const io = (this.io = new (window as any).IntersectionObserver(
        (data: IntersectionObserverEntry[]) => {
          if (data[0].isIntersecting || (data[1] && data[1].isIntersecting)) {
            io.disconnect();
            this.io = undefined;
            cb();
          }
        },
        { rootMargin }
      ));

      io.observe(el);
    } else {
      // browser doesn't support IntersectionObserver
      // so just fallback to always show it
      cb();
    }
  }

  @Watch('name')
  @Watch('src')
  @Watch('icon')
  loadIcon() {
    if (Build.isBrowser && this.isVisible) {
      const url = getUrl(this);
      if (url) {
        if (iconContent.has(url)) {
          // sync if it's already loaded
          this.svgContent = iconContent.get(url);
          this.isLoading = false;
        } else {
          // async if it hasn't been loaded
          getSvgContent(url).then(() => {
            this.svgContent = iconContent.get(url);
            this.isLoading = false;
          });
        }
      }
    }
    this.setAriaLabel();
  }

  render() {
    const flipRtl =
      this.flipRtl ||
      (this.name &&
        (this.name.indexOf('arrow') > -1 ||
          this.name.indexOf('chevron') > -1) &&
        (this.el.ownerDocument as Document).dir === 'rtl' &&
        this.flipRtl !== false);

    return (
      <Host
        role="img"
        class={{
          ...createColorClasses(this.color),
          loading: this.isLoading,
          [`icon-${this.size}`]: !!this.size,
          'flip-rtl': !!flipRtl,
        }}
      >
        {Build.isBrowser && this.svgContent ? (
          <div class="icon-inner" innerHTML={this.svgContent}></div>
        ) : (
          <div class="icon-inner"></div>
        )}
      </Host>
    );
  }
}
